import { Observable } from 'rxjs/Rx'
import { Subject } from 'rxjs/Subject'
import { ModelRepository } from './model-repository'
import { BaseModel } from '../models/base-model'
import { IModelRepositoryAdapter } from './'
import { TypeOf, ObservableUtils } from '..'

export abstract class ModelRepositoryAdapter<T extends BaseModel> implements IModelRepositoryAdapter<T> {

  private _methodHooks: {
    [action: string]: {
      before: Array<(currentModel: BaseModel, oldModel: BaseModel, ...options) => void>,
      after: Array<(currentModel: BaseModel, oldModel: BaseModel, ...options) => void>
    }
  }
  private _eventEmitters: WeakMap < T, {[action: string]: Subject<T>} >
  private ALL_MODELS_KEY: T = Object.freeze({}) as T
  private _initializeObservable = Observable.of({})

  public get modelType() {
    return this.ModelType
  }

constructor(
  private _repository: ModelRepository,
  private ModelType: TypeOf<T>,
  private _observableUtils: ObservableUtils = new ObservableUtils()
) {
    this._methodHooks = {}
    this._eventEmitters = new WeakMap<T, {[action: string]: Subject<T>}>()

    this.registerBeforeAfterEmitters()
  }

  public findModelById(id: string, includeRelations: string[] = []): Observable <T> {
    return this._initializeObservable.concatMap(() => this._repository.findModelById(this.ModelType.name, id, includeRelations))  as Observable<T>
  }

  public findModelByIdWithRelationships(id: string): Observable <T> {
    return this.findModelById(id)
  }

  public findAllModels(includeRelations: string[] = []): Observable < T[] > {
    return this._initializeObservable.concatMap(() => this._repository.findAllModels(this.ModelType.name, includeRelations)) as Observable<T[]>
  }

  public findAllModelsWithRelationships(): Observable < T[] > {
    return this.findAllModels([])
  }

  public findModelsBy(searchCriteria: any, includeRelations: string[] = []): Observable < T | T[] > {
    return this._initializeObservable.concatMap(() => this._repository.findModelsBy(this.ModelType.name, searchCriteria, includeRelations)) as Observable<T|T[]>
  }

  public createModel(properties: {[key: string]: any}): T {
    return this._repository.createModel(this.modelType.name, properties) as T
  }

  public save(model: T): Observable <T> {
    const action = model.isPersisted() ? 'Update' : 'Create'

    const result = this._repository.save(model).map((newModel: T) => {
      this.executeMethodHooks('after', 'save', newModel, model, action)
      return newModel
    })
    this.executeMethodHooks('before', 'save', model)
    return result as Observable<T>
  }

  public delete(model: T): Observable<boolean> {
    const result = this._repository.delete(model)
      .map((deletedModel: boolean) => {
        this.executeMethodHooks('after', 'delete', {} as T, model)
        return deletedModel
      })
    this.executeMethodHooks('before', 'delete', model)
    return result
  }

  public onBeforeCreate(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onBeforeCreate', model) }
  public onAfterCreate(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onAfterCreate', model) }

  public onBeforeUpdate(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onBeforeUpdate', model) }
  public onAfterUpdate(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onAfterUpdate', model) }

  public onBeforeSave(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onBeforeSave', model) }
  public onAfterSave(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onAfterSave', model) }

  public onBeforeDelete(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onBeforeDelete', model) }
  public onAfterDelete(model?: T): Observable <T> { return this.getOrCreateBeforeAfterEmitter('onAfterDelete', model) }

  protected initialize(includes: string[] = [], filters: any = {}): void {
    this._initializeObservable = this._observableUtils.lazyObservable<BaseModel|BaseModel[]>(
      () => this._repository
        .findModelsBy(this.ModelType.name, filters, includes)
        .do(() => this._initializeObservable = Observable.of({}))
        .share()
    )
  }

  protected addMethodHook(
    when: 'before' | 'after', repositoryFnName: 'delete' | 'save',
    actionFn: (model: T, oldModel?: T, ...options: any[]) => void
  ) {

    if (typeof this._methodHooks[repositoryFnName] === 'undefined') {
      this._methodHooks[repositoryFnName] = { before: [], after: []}
    }
    this._methodHooks[repositoryFnName][when].push(actionFn)
  }

  private executeMethodHooks(
    when: 'before' | 'after', repositoryFnName: 'delete' | 'save',
    currentModel: T, oldModel ?: T, ...options: any[]
  ) {

    (this._methodHooks[repositoryFnName] || { before: [], after: []})[when]
    .forEach((actionFn) => actionFn(currentModel, oldModel, ...options))
  }

  private registerBeforeAfterEmitters() {
    this.addMethodHook('before', 'save', (model) => {
      const action = model.isPersisted() ? 'Update' : 'Create'
      this.emitBeforeAfterEvent(`onBefore${action}`, model)
    })
    this.addMethodHook('after', 'save', (model, _oldModel, action) => this.emitBeforeAfterEvent(`onAfter${action}`, model))
    this.addMethodHook('before', 'delete', (model) => this.emitBeforeAfterEvent('onBeforeDelete', model))
    this.addMethodHook('after', 'delete', (model, _oldModel, _action) => {
      this.emitBeforeAfterEvent('onAfterDelete', model)
      this.deleteEventEmitterFor(model)
    })
  }

  private emitBeforeAfterEvent(eventListenerFnName: string, model: T) {
    this.getBeforeAfterEmitters(eventListenerFnName, model).forEach((emitter: Subject<T>) => emitter.next(model))
  }

  private getBeforeAfterEmitters(eventListenerFnName: string, model: T = this.ALL_MODELS_KEY): Array < Subject <T>> {
    const thisEmitter = (this._eventEmitters.get(model) || {})[eventListenerFnName]
    const emitters = (typeof thisEmitter !== 'undefined') ? [ thisEmitter ] : []

    return model === this.ALL_MODELS_KEY ? emitters : emitters.concat(this.getBeforeAfterEmitters(eventListenerFnName))
  }

  private getOrCreateBeforeAfterEmitter(eventListenerFnName: string, model: T = this.ALL_MODELS_KEY): Subject <T> {

    let emitters = this._eventEmitters.get(model) || {}
    if (typeof emitters[eventListenerFnName] === 'undefined') {
      let subject = new Subject<T>()
      if (eventListenerFnName.indexOf('Save') !== -1) {
        subject = this.getOrCreateBeforeAfterEmitter(eventListenerFnName.replace('Save', 'Create'), model)
                  .merge(
                    this.getOrCreateBeforeAfterEmitter(eventListenerFnName.replace('Save', 'Update'), model)
                  ) as Subject<T>
        emitters = this._eventEmitters.get(model) || {}
      }
      emitters[eventListenerFnName] = subject
      this._eventEmitters.set(model, emitters)
    }
    return emitters[eventListenerFnName]
  }

  private deleteEventEmitterFor(model: T) {
    const emitters = this._eventEmitters.get(model)
    if (typeof emitters !== 'undefined') {
      Object.keys(emitters).forEach((eventListenerFnName: string) => emitters[eventListenerFnName]['dispose']())
      this._eventEmitters.delete(model)
    }
  }

}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/model-repository-adapter.ts